import { hashKey, type QueryFunctionContext } from '@tanstack/react-query'
import { api } from './api'

// Scope cache and request to the same person, including while the new query loads.
export function calendarMeetingsQuery(
  sessionUserId: string | undefined,
  viewAsUserId: string,
  from: Date,
  to: Date,
) {
  const userId = viewAsUserId || sessionUserId
  const scope = ['meetings', 'calendar', sessionUserId, userId] as const
  return {
    queryKey: [...scope, from.toISOString(), to.toISOString()],
    queryFn: async ({
      client,
      queryKey,
      signal,
    }: QueryFunctionContext): Promise<
      Awaited<ReturnType<typeof api.listMeetings>> & { accessDenied?: true }
    > => {
      try {
        return await api.listMeetings({ from: from.toISOString(), to: to.toISOString(), userId })
      } catch (error) {
        if (error instanceof Error && 'status' in error && error.status === 403) {
          const denied = { meetings: [], accessDenied: true as const }
          if (!signal.aborted) {
            // Permission is person-wide. Cancel sibling ranges before replacing
            // their data, so neither cancellation rollback nor late responses
            // can restore private meetings. Keep mounted observers attached.
            await client.cancelQueries({
              queryKey: scope,
              predicate: (query) => query.queryHash !== hashKey(queryKey),
            })
            // Also drain an already resolved sibling's queued cache write.
            // Another denial may have cancelled us while awaiting cancellation.
            if (!signal.aborted) client.setQueriesData({ queryKey: scope }, denied)
          }
          // An already cancelled request must not revoke a later regrant.
          return denied
        }
        throw error
      }
    },
    enabled: !!sessionUserId,
  }
}
